/**
 * DashboardBanners — prioritized setup-nag stack.
 *
 * Replaces the legacy SetupNagBanner (stacked WhatsApp + Ad-platform with
 * no priority) with the locked Phase 3 rule (see PLAN_DASHBOARD_REACT_PORT.md
 * Q1):
 *
 *   - One amber setup banner at a time, in sequential order:
 *     WhatsApp > Ad-platform. They're sequential setup steps; show only
 *     the next blocker, not everything-that-could-block.
 *   - The blue Snippet banner is independent of the amber sequence.
 *     Can co-exist with one amber banner. Suppressed when EITHER
 *     snippet_skipped_at OR no_website_at is set (or snippet is verified).
 *   - Max 2 banners visible at once, not 3.
 *
 * Visibility rules per banner condition:
 *   - WhatsApp     → !me.waba_id (not paired)
 *   - Ad-platform  → no Google/Meta connected AND no skip set on either
 *                    (a skip declares "I don't run this platform"; don't
 *                    nag if they've actively skipped both)
 *   - Snippet      → !snippet_verified AND !snippet_skipped AND !no_website
 *
 * Admin without view-as sees no banners (they have no platforms of their
 * own; the banners would be noise on the admin view).
 *
 * Preview spec: heymotek-web/src/pages/preview/dashboard/home.astro.
 */

import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MessageCircle, AlertCircle, Code2 } from 'lucide-react';
import { useAuth } from '@/lib/useAuth';
import { apiRequest } from '@/lib/api';

export function DashboardBanners() {
  const { me, viewAsClientId } = useAuth();
  const queryClient = useQueryClient();

  const dismissNoWebsite = useMutation({
    mutationFn: () =>
      apiRequest('/onboarding/skip', {
        method: 'POST',
        body: { step: 'no_website' },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });

  if (!me) return null;
  if (me.is_system_admin && !viewAsClientId) return null;

  // Amber sequence — show one, in priority order.
  const showWhatsappBanner = !me.waba_id;
  const adPlatformNeeded =
    !me.google_ads_customer_id &&
    !me.facebook_pixel_id &&
    !me.google_ads_skipped_at &&
    !me.facebook_skipped_at;
  const showAdPlatformBanner = !showWhatsappBanner && adPlatformNeeded;

  // Snippet — independent (can co-exist with one amber).
  const snippetSettled =
    !!me.snippet_verified_at || !!me.snippet_skipped_at || !!me.no_website_at;
  const showSnippetBanner = !snippetSettled;

  if (!showWhatsappBanner && !showAdPlatformBanner && !showSnippetBanner) {
    return null;
  }

  return (
    <div className="space-y-2">
      {showWhatsappBanner && (
        <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
          <MessageCircle
            className="mt-0.5 h-5 w-5 shrink-0 text-amber-700"
            strokeWidth={2}
          />
          <div className="min-w-0 flex-1">
            <p className="text-base font-semibold text-dark">
              Connect WhatsApp to start matching contacts
            </p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              HeyMotek can't see contacts until your WhatsApp is paired.
            </p>
          </div>
          <Link
            to="/onboarding/go-live"
            className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-amber-700 px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-amber-800"
          >
            Connect
          </Link>
        </div>
      )}

      {showAdPlatformBanner && (
        <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
          <AlertCircle
            className="mt-0.5 h-5 w-5 shrink-0 text-amber-700"
            strokeWidth={2}
          />
          <div className="min-w-0 flex-1">
            <p className="text-base font-semibold text-dark">
              Connect Google Ads or Meta Ads to fire conversions back
            </p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Attribution works without them, but your ad platforms won't get
              smarter.
            </p>
          </div>
          <Link
            to="/onboarding/connect"
            className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-amber-700 px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-amber-800"
          >
            Connect
          </Link>
        </div>
      )}

      {showSnippetBanner && (
        <div className="flex items-start gap-3 rounded-2xl border border-info/30 bg-info/5 px-4 py-3">
          <Code2
            className="mt-0.5 h-5 w-5 shrink-0 text-info"
            strokeWidth={2}
          />
          <div className="min-w-0 flex-1">
            <p className="text-base font-semibold text-dark">
              Install the snippet for full attribution
            </p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Lets us match website visitors to their WhatsApp contacts.
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => dismissNoWebsite.mutate()}
              disabled={dismissNoWebsite.isPending}
              className="inline-flex items-center rounded-lg border border-dark/15 bg-white px-3 py-1.5 text-xs font-bold text-dark transition-colors hover:border-dark/30 disabled:opacity-50"
            >
              I don't have a website
            </button>
            <Link
              to="/integrations"
              className="inline-flex items-center gap-1 rounded-lg bg-info px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-info/90"
            >
              Install
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
